import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces'
import {
	convertDateFromStringToMoment,
	convertStringDateFormat,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import { STORE_TREATMENT_METHOD } from 'Portals/Customer/constants'
import { Dashboard } from 'Portals/Customer/entities'
import {
	BANNER_VISIBILITY_KEY,
	BANNER_VISIBILITY_VALUE
} from 'Portals/Customer/Pages/Dashboard/common/entities/Banner/utils'
import { arrayBufferToBase64 } from 'Portals/Customer/Pages/Dashboard/common/entities/Banner/utils/utils'
import { DashboardRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

export class DashboardStore {
	rootStore: RootStore
	@observable accessor error: AxiosError | null = null
	@observable accessor landfills = getDefaultStoreArrayValue<Dashboard.Landfill>()
	@observable accessor serviceSiteLandfills = getDefaultStoreArrayValue<Dashboard.ServiceSiteLandfill>()
	@observable accessor invoiceStatement = getDefaultStoreArrayValue<Dashboard.InvoiceStatement.Basic>()
	@observable accessor landfillStatistics = getDefaultStoreValue<Dashboard.LandfillStatictics>()
	@observable accessor totalSpent = getDefaultStoreValue<Dashboard.TotalSpent>()
	@observable accessor title = getDefaultStoreArrayValue<string>()
	@observable accessor orderSummary = getDefaultStoreArrayValue<Dashboard.OrderSummary>()
	@observable accessor waitingForResponse = getDefaultStoreArrayValue<Dashboard.WaitingForResponse>()
	@observable accessor profileServiceSites = getDefaultStoreArrayValue<Dashboard.ProfileServiceSite>()
	@observable accessor banners = getDefaultStoreArrayValue<Dashboard.Banner.Extended>()

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore
	}

	@computed
	get shouldShowBanners() {
		return (
			this.banners.value.length > 0 &&
			sessionStorage.getItem(BANNER_VISIBILITY_KEY) !== BANNER_VISIBILITY_VALUE
		)
	}

	@action.bound
	hideBanners() {
		this.clearBanners()
		sessionStorage.setItem(BANNER_VISIBILITY_KEY, BANNER_VISIBILITY_VALUE)
	}

	@flow.bound
	*retrieveBanners(): Generator<Promise<Dashboard.Banner.Basic[] | ArrayBuffer | string>> {
		this.error = null
		this.banners.isLoading = true

		try {
			const banners = yield DashboardRepo.retrieveBanners()

			this.banners.value = []

			for (const banner of banners) {
				const arrayBuffer = yield DashboardRepo.retrieveBanner(banner.id)

				const base64Image = yield arrayBufferToBase64(arrayBuffer)

				this.banners.value = [
					...this.banners.value,
					{
						...banner,
						image: base64Image
					}
				] as Dashboard.Banner.Extended[]
			}
		} catch (error) {
			this.error = error as AxiosError
		} finally {
			this.banners.isLoading = false
		}
	}

	@flow.bound
	*retrieveDashboardTitle(): CustomGenerator<string[]> {
		yield requestWrapper({
			field: this.title,
			repository: DashboardRepo.retrieveDashboardTitle,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveDataWithNoPermissionsRequired() {
		this.error = null
		try {
			yield Promise.all([
				this.rootStore[STORE_TREATMENT_METHOD].retrieveTreatmentMethodsMostUsed(),
				this.retrieveLandfills(),
				this.retrieveServiceSiteLandfills(),
				this.retrieveLandfillStatistics()
			])
		} catch (error) {
			this.error = error as AxiosError
		}
	}

	@flow.bound
	*retrieveLandfills(): CustomGenerator<Dashboard.Landfill[]> {
		yield requestWrapper({
			field: this.landfills,
			repository: DashboardRepo.retrieveLandfills,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveProfileServiceSite(): CustomGenerator<Dashboard.ProfileServiceSite[]> {
		yield requestWrapper({
			field: this.profileServiceSites,
			repository: DashboardRepo.retrieveProfileServiceSite,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveInvoiceStatement(): CustomGenerator<Dashboard.InvoiceStatement.Basic[]> {
		yield requestWrapper({
			field: this.invoiceStatement,
			repository: DashboardRepo.retrieveInvoiceStatement,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveLandfillStatistics(): CustomGenerator<Dashboard.LandfillStatictics> {
		yield requestWrapper({
			field: this.landfillStatistics,
			repository: DashboardRepo.retrieveLandfillStatistics,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveTotalSpent(): CustomGenerator<Dashboard.TotalSpent> {
		yield requestWrapper({
			field: this.totalSpent,
			repository: DashboardRepo.retrieveTotalSpent,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveServiceSiteLandfills(): CustomGenerator<Dashboard.ServiceSiteLandfill[]> {
		yield requestWrapper({
			field: this.serviceSiteLandfills,
			repository: DashboardRepo.retrieveServiceSiteLandfills,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@computed
	get formattedLandfillStatistics() {
		const { value } = this.landfillStatistics
		return {
			nonLandfill: value?.nonLandfill || 0,
			landfill: value?.landfill || 0,
			nonLandfillPercent: value?.nonLandfillPercent || 0,
			landfillPercent: value?.landfillPercent || 0,
			totalSpent: this.totalSpent.value?.totalSpent || 0,
			totalSpentCurrencyCode: this.totalSpent.value?.currencyCode || '',
			total: value?.total || 0,
			totalEarning: null
		}
	}

	@computed
	get formattedServiceSiteLandfills() {
		const MAX_SERVICE_SITE_LANDFILLS = 4
		return this.serviceSiteLandfills.value.slice(0, MAX_SERVICE_SITE_LANDFILLS)
	}

	@computed
	get formattedLandfills() {
		return this.landfills.value.map((item) => ({
			label: convertDateFromStringToMoment(item.date)?.format('MMM YYYY') || '',
			value1: item.landfillShare,
			value2: item.nonLandfillShare
		}))
	}

	@computed
	get formattedOrderSummary() {
		return this.orderSummary.value.map((order) => ({
			value: order.value,
			descriptor: convertStringDateFormat(
				order.descriptor,
				this.rootStore[STORE_UI].getCurrentDateFormat
			)
		}))
	}

	@flow.bound
	*retrieveOrderSummary(): CustomGenerator<Dashboard.OrderSummary[]> {
		yield requestWrapper({
			field: this.orderSummary,
			repository: DashboardRepo.retrieveOrderSummary,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveOrderWaitingForResponse(): CustomGenerator<Dashboard.WaitingForResponse[]> {
		yield requestWrapper({
			field: this.waitingForResponse,
			repository: DashboardRepo.retrieveOrderWaitingForResponse,
			repositoryParams: null
		})
	}

	@action.bound
	clearBanners() {
		this.banners = getDefaultStoreArrayValue<Dashboard.Banner.Extended>()
	}
}
